// =============================================================================
// BP-07-03: SavedResourcesPage
// User's saved resources with folder organization
// =============================================================================

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Bookmark,
  FolderPlus,
  Search,
  Grid,
  List,
  MoreVertical,
  Trash2,
  FolderOpen,
  Loader2,
} from 'lucide-react';
import { useSavedResources, useSavedResourceFolders, useUnsaveResource } from '@/features/resources/hooks/useSavedResources';
import { ResourceCard, ResourceGrid, ResourceSearchBar } from '../components';
import { useViewStore } from '@/features/resources/stores/resourceStore';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function SavedResourcesPage() {
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);

  // View mode from store
  const { viewMode, setViewMode } = useViewStore();

  // Fetch saved resources
  const {
    data: savedResources,
    isLoading,
    error,
  } = useSavedResources({ folder: selectedFolder || undefined });

  // Fetch folders
  const { data: folders } = useSavedResourceFolders();

  // Unsave mutation
  const { mutate: unsaveResource } = useUnsaveResource();

  // Filter by search
  const filteredResources = savedResources?.filter((saved) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      saved.resource.title.toLowerCase().includes(query) ||
      saved.resource.excerpt?.toLowerCase().includes(query) ||
      saved.notes?.toLowerCase().includes(query)
    );
  });

  // Handle unsave
  const handleUnsave = (resourceId: string) => {
    if (window.confirm('Remove this resource from your saved items?')) {
      unsaveResource(resourceId);
    }
  };

  return (
    <>
      <Helmet>
        <title>Saved Resources | BrightPath</title>
        <meta
          name="description"
          content="View and organize your saved resources."
        />
      </Helmet>

      <div className="min-h-screen bg-cream">
        {/* Header */}
        <section className="bg-white border-b border-warm-gray/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="font-display text-2xl md:text-3xl font-semibold text-navy flex items-center gap-3">
                  <Bookmark className="w-8 h-8 text-sage" />
                  Saved Resources
                </h1>
                <p className="text-warm-gray mt-1">
                  {savedResources?.length || 0} saved items
                </p>
              </div>

              <div className="flex items-center gap-3">
                {/* Search */}
                <div className="relative flex-1 md:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search saved..."
                    className="w-full pl-9 pr-4 py-2 bg-cream border border-warm-gray/20 rounded-lg text-sm focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/20"
                  />
                </div>

                {/* View Mode Toggle */}
                <div className="flex items-center bg-cream rounded-lg p-1">
                  <button
                    type="button"
                    onClick={() => setViewMode('grid')}
                    className={cn(
                      'p-2 rounded-md transition-colors',
                      viewMode === 'grid'
                        ? 'bg-white shadow-sm text-sage'
                        : 'text-warm-gray hover:text-navy'
                    )}
                    aria-label="Grid view"
                  >
                    <Grid className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('list')}
                    className={cn(
                      'p-2 rounded-md transition-colors',
                      viewMode === 'list'
                        ? 'bg-white shadow-sm text-sage'
                        : 'text-warm-gray hover:text-navy'
                    )}
                    aria-label="List view"
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>

                {/* New Folder Button */}
                <button
                  type="button"
                  onClick={() => setShowNewFolderModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
                >
                  <FolderPlus className="w-4 h-4" />
                  <span className="hidden sm:inline text-sm font-medium">New Folder</span>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex gap-8">
              {/* Folders Sidebar */}
              <aside className="hidden md:block w-56 flex-shrink-0">
                <nav className="sticky top-24 space-y-1">
                  <button
                    type="button"
                    onClick={() => setSelectedFolder(null)}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-left transition-colors',
                      selectedFolder === null
                        ? 'bg-sage text-white'
                        : 'text-navy hover:bg-cream'
                    )}
                  >
                    <Bookmark className="w-5 h-5" />
                    <span className="font-medium">All Saved</span>
                    <span className={cn(
                      'ml-auto text-sm',
                      selectedFolder === null ? 'text-white/70' : 'text-warm-gray'
                    )}>
                      {savedResources?.length || 0}
                    </span>
                  </button>

                  {folders && folders.length > 0 && (
                    <>
                      <div className="pt-4 pb-2 px-4">
                        <span className="text-xs font-semibold text-warm-gray uppercase tracking-wide">
                          Folders
                        </span>
                      </div>
                      {folders.map((folder) => (
                        <button
                          key={folder}
                          type="button"
                          onClick={() => setSelectedFolder(folder)}
                          className={cn(
                            'w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-left transition-colors',
                            selectedFolder === folder
                              ? 'bg-sage text-white'
                              : 'text-navy hover:bg-cream'
                          )}
                        >
                          <FolderOpen className="w-5 h-5" />
                          <span className="font-medium truncate">{folder}</span>
                        </button>
                      ))}
                    </>
                  )}
                </nav>
              </aside>

              {/* Resources */}
              <div className="flex-1 min-w-0">
                {/* Mobile Folder Select */}
                <div className="md:hidden mb-6">
                  <select
                    value={selectedFolder || ''}
                    onChange={(e) => setSelectedFolder(e.target.value || null)}
                    className="w-full px-4 py-2.5 bg-white border border-warm-gray/20 rounded-lg text-navy focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/20"
                  >
                    <option value="">All Saved</option>
                    {folders?.map((folder) => (
                      <option key={folder} value={folder}>
                        {folder}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Loading State */}
                {isLoading && (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-sage animate-spin" />
                  </div>
                )}

                {/* Error State */}
                {error && (
                  <div className="text-center py-12">
                    <p className="text-red-600 mb-4">
                      Unable to load saved resources.
                    </p>
                    <button
                      type="button"
                      onClick={() => window.location.reload()}
                      className="px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
                    >
                      Retry
                    </button>
                  </div>
                )}

                {/* Empty State */}
                {!isLoading && !error && (!filteredResources || filteredResources.length === 0) && (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 rounded-full bg-sage/10 flex items-center justify-center mx-auto mb-4">
                      <Bookmark className="w-8 h-8 text-sage" />
                    </div>
                    <h2 className="font-display text-xl font-semibold text-navy mb-2">
                      {searchQuery
                        ? 'No matching resources'
                        : selectedFolder
                        ? `No resources in "${selectedFolder}"`
                        : 'No saved resources yet'}
                    </h2>
                    <p className="text-warm-gray mb-6 max-w-md mx-auto">
                      {searchQuery
                        ? 'Try adjusting your search terms.'
                        : 'Save resources from articles and guides to access them quickly later.'}
                    </p>
                    {!searchQuery && (
                      <Link
                        to="/resources"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
                      >
                        Browse Resources
                      </Link>
                    )}
                  </div>
                )}

                {/* Resources Grid/List */}
                {!isLoading && filteredResources && filteredResources.length > 0 && (
                  <div
                    className={cn(
                      viewMode === 'grid'
                        ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'
                        : 'space-y-4'
                    )}
                  >
                    {filteredResources.map((saved) => (
                      <SavedResourceCard
                        key={saved.id}
                        saved={saved}
                        viewMode={viewMode}
                        onUnsave={() => handleUnsave(saved.resource.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* New Folder Modal */}
      {showNewFolderModal && (
        <NewFolderModal onClose={() => setShowNewFolderModal(false)} />
      )}
    </>
  );
}

// -----------------------------------------------------------------------------
// SavedResourceCard
// -----------------------------------------------------------------------------

interface SavedResourceCardProps {
  saved: {
    id: string;
    resource: any;
    notes?: string;
    folder?: string;
    createdAt: string;
  };
  viewMode: 'grid' | 'list';
  onUnsave: () => void;
}

function SavedResourceCard({ saved, viewMode, onUnsave }: SavedResourceCardProps) {
  const [showMenu, setShowMenu] = useState(false);

  if (viewMode === 'list') {
    return (
      <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-warm-gray/10 hover:shadow-soft transition-shadow">
        {/* Image */}
        {saved.resource.featuredImageUrl && (
          <Link
            to={`/resources/${saved.resource.slug}`}
            className="flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden bg-cream"
          >
            <img
              src={saved.resource.featuredImageUrl}
              alt=""
              className="w-full h-full object-cover"
            />
          </Link>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          <Link
            to={`/resources/${saved.resource.slug}`}
            className="font-display text-lg font-semibold text-navy hover:text-sage line-clamp-1"
          >
            {saved.resource.title}
          </Link>
          {saved.resource.excerpt && (
            <p className="text-warm-gray text-sm mt-1 line-clamp-2">
              {saved.resource.excerpt}
            </p>
          )}
          {saved.notes && (
            <p className="text-sm text-sage mt-2 italic">
              Note: {saved.notes}
            </p>
          )}
          <div className="flex items-center gap-4 mt-2 text-xs text-warm-gray">
            <span>
              Saved {new Date(saved.createdAt).toLocaleDateString()}
            </span>
            {saved.folder && (
              <span className="flex items-center gap-1">
                <FolderOpen className="w-3 h-3" />
                {saved.folder}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="relative flex-shrink-0">
          <button
            type="button"
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 hover:bg-cream rounded-lg transition-colors"
          >
            <MoreVertical className="w-5 h-5 text-warm-gray" />
          </button>
          {showMenu && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowMenu(false)}
              />
              <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-warm-gray/20 rounded-lg shadow-lifted z-50">
                <button
                  type="button"
                  onClick={() => {
                    setShowMenu(false);
                    onUnsave();
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-red-600 hover:bg-red-50 transition-colors text-left"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Remove</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Grid view
  return (
    <div className="relative group">
      <ResourceCard resource={saved.resource} />
      
      {/* Overlay actions on hover */}
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          onClick={onUnsave}
          className="p-2 bg-white/90 backdrop-blur-sm rounded-lg shadow-soft hover:bg-red-50 transition-colors"
          title="Remove from saved"
        >
          <Trash2 className="w-4 h-4 text-red-500" />
        </button>
      </div>

      {/* Note indicator */}
      {saved.notes && (
        <div className="absolute bottom-2 left-2 px-2 py-1 bg-sage/90 text-white text-xs rounded">
          Has note
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// NewFolderModal
// -----------------------------------------------------------------------------

interface NewFolderModalProps {
  onClose: () => void;
}

function NewFolderModal({ onClose }: NewFolderModalProps) {
  const [folderName, setFolderName] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement folder creation
    console.log('Create folder:', folderName);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-navy/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lifted max-w-md w-full p-6">
        <h2 className="font-display text-xl font-semibold text-navy mb-4">
          Create New Folder
        </h2>
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            placeholder="Folder name..."
            className="w-full px-4 py-3 bg-cream border-2 border-transparent rounded-lg focus:outline-none focus:border-sage"
            autoFocus
          />
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-warm-gray hover:text-navy transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!folderName.trim()}
              className="px-6 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SavedResourcesPage;


